import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useContactCompanyLinkStore } from '../../../lib/stores/contactCompanyLinkStore';
import { ContactCompanyLinkPicker } from '../../clients/ContactCompanyLinkPicker';
import type { ClientRelationship } from '../../../lib/types';

interface Props {
  /** id dell'azienda-cliente del deal (target dei link 'contatto') */
  companyClientId: string;
  /** id persona selezionati per il deal */
  selectedPersonIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Selettore dei referenti (persone) del deal, modello Contatti/Aziende first-class.
 * Mostra le persone collegate all'azienda-cliente via client_relationships('contatto'),
 * selezionabili come "contatti dell'offerta" (deal_contacts.person_client_id).
 * Consente di crearne di nuove (cerca-o-crea) e collegarle.
 */
export function DealReferentiSelector({ companyClientId, selectedPersonIds, onSelectionChange }: Props) {
  const { fetchForCompany, linkPersonToCompany } = useContactCompanyLinkStore();
  const [links, setLinks] = useState<ClientRelationship[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (companyClientId) {
      setLoading(true);
      fetchForCompany(companyClientId).then(rows => {
        if (alive) { setLinks(rows); setLoading(false); }
      });
    }
    return () => { alive = false; };
  }, [companyClientId, fetchForCompany]);

  const toggle = (personId: string, checked: boolean) => {
    onSelectionChange(checked
      ? [...selectedPersonIds, personId]
      : selectedPersonIds.filter(id => id !== personId));
  };

  return (
    <div className="space-y-1.5">
      {loading && <p className="text-[12px] text-black/40 dark:text-white/40">Caricamento referenti…</p>}
      {!loading && links.length === 0 && !showPicker && (
        <p className="text-[12px] text-black/40 dark:text-white/40">Nessun referente collegato.</p>
      )}
      {links.map(link => {
        const p: any = (link as any).person;
        if (!p) return null;
        const checked = selectedPersonIds.includes(p.id);
        return (
          <label key={link.id} className="flex items-center gap-2 text-[13px] cursor-pointer text-[#37352f] dark:text-white">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => toggle(p.id, e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span className="truncate">{p.business_name || p.email}</span>
            {link.role && <span className="text-black/40 dark:text-white/40">· {link.role}</span>}
          </label>
        );
      })}

      {showPicker ? (
        <ContactCompanyLinkPicker
          targetEntityType="individual"
          excludeIds={links.map(l => (l as any).person?.id).filter(Boolean)}
          onPick={async (person) => {
            await linkPersonToCompany({ person_client_id: person.id, company_client_id: companyClientId });
            const rows = await fetchForCompany(companyClientId);
            setLinks(rows);
            onSelectionChange([...selectedPersonIds, person.id]);
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="inline-flex items-center gap-1 text-[12px] text-[#2383E2] hover:underline"
        >
          <Plus className="w-3 h-3" /> Aggiungi referente
        </button>
      )}
    </div>
  );
}
